import dayjs from 'dayjs';
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bar, CartesianGrid, ComposedChart, Label, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import InfoPopup from '~/components/InfoPopup';
import { useAuth } from '~/routes/components/AuthProvider';
import { capitalizeFirstLetter, formatNumber, formatNumberWithoutFractions, getPrefixData } from '~/utils/helpers';
import { ChartContainer, TooltipWrapper } from './style';
import { BASE_PRICE_TEXT_SLUG } from '~/Constants';

interface DashboardChartProps {
  chartData: any
  link: string
  info?: string
  prefix: string
}

class CustomizedYAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, payload, fill } = this.props;
    const numberOfSpacesBetweenLabelAndGraph = (parseInt(payload.value) / 1000).toString().length * 10 + 2
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={numberOfSpacesBetweenLabelAndGraph < 40 ? 40 : numberOfSpacesBetweenLabelAndGraph}
          y={0} dy={4} textAnchor='end' fill={fill} transform='rotate(0)'>
          {(parseInt(payload.value) / 1000) < 1 ? `${formatNumberWithoutFractions(parseInt(payload.value))}` :
            `${formatNumberWithoutFractions(parseInt(payload.value) / 1000)}k`}
        </text>
      </g>
    );
  }
}

const DashboardChart: React.FC<DashboardChartProps> = (props) => {
  const { chartData, prefix } = props
  const { t } = useTranslation()
  const { systemData, systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const basePriceText = systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG)
  const { firstColor } = useContext(ThemeContext)
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`

  const prefixData = getPrefixData(prefix)

  const info = t('Overview over the fixed price volumes your dairy has confirmed (including average fixed prices) for the according trading months. You can find all the details under Reporting.')

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const confirmedAmount = payload.find(item => item.dataKey === 'confirmedAmount')
      const remainingAmount = payload.find(item => item.dataKey === 'remainingAmount')
      const offeredNotConfirmedAmount = formatNumberWithoutFractions(payload[0].payload.offeredNotConfirmedAmount)
      const maxAmount = confirmedAmount.value + remainingAmount.value
      const maxPrice = formatNumber(payload[0].payload.maxPrice)
      const minPrice = formatNumber(payload[0].payload.minPrice)
      const percentAmount = formatNumberWithoutFractions(confirmedAmount.value / parseFloat(maxAmount) * 100)
      return <TooltipWrapper>
        <div style={{ padding: 5 }} key={label}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Procurement month: {{label}} ({{percentAmount}}%)', { label, percentAmount })}</p>
        </div>
        {payload.map(item => {
          const value = item.unit && item.unit === amountUnit ? formatNumberWithoutFractions(item.value) : formatNumber(item.value)
          return <div style={{ padding: 5 }} key={item.name}>
            <p className='tooltipTitle' style={{ color: item.color }}>{`${item.name}: ${value} ${item.unit || ''}`}</p>
          </div>
        })}
        <div style={{ padding: 5 }} key={'max-amount'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Max amount: {{maxAmount}} {{amountUnit}}',
            { maxAmount: formatNumberWithoutFractions(maxAmount), amountUnit })}</p>
        </div>
        <div style={{ padding: 5 }} key={'offered-amount'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Offered not confirmed amount: {{offeredNotConfirmedAmount}} {{amountUnit}}',
            { offeredNotConfirmedAmount, amountUnit })}</p>
        </div>

        <div style={{ padding: 5 }} key={'max-price'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.max}: ${maxPrice} ${priceLabel}`}</p>
        </div>
        <div style={{ padding: 5 }} key={'min-price'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.min}: ${minPrice} ${priceLabel}`}</p>
        </div>
      </TooltipWrapper>
    }

    return null;
  };

  const data = chartData.map((item) => {
    return {
      ...item,
      avgPrice: item.avgPrice ? Number(item.avgPrice).toFixed(2) : null,
      offeredAmount: parseFloat(formatNumber(item.offeredAmount)),
      month: capitalizeFirstLetter(dayjs(item.month).format('MMM YYYY')),
      maxAmount: parseInt(item.maxAmount),
      remainingAmount: parseInt(item.remainingAmount),
      confirmedAmount: parseInt(item.confirmedAmount)
    }
  })

  const domainWithoutNullValues = data.map(item => item.avgPrice ? item : null).filter(item => !!item)
  const domainWithoutNullValuesBase = data.map(item => item.basePrice ? item : null).filter(item => !!item)
  const minAvg = Math.min(...domainWithoutNullValues.map(item => item.avgPrice))
  const maxAvg = Math.max(...domainWithoutNullValues.map(item => item.avgPrice))
  const minBase = Math.min(...domainWithoutNullValuesBase.map(item => item.basePrice))
  const maxBase = Math.max(...domainWithoutNullValuesBase.map(item => item.basePrice))
  const min = minAvg < minBase ? minAvg : minBase
  const max = maxBase > maxAvg ? maxBase : maxAvg

  return <ChartContainer>
    <div className='header'>
      {t('Trading activity trading slot')}
      <InfoPopup
        content={props.info || info}
        styleIcon={{ cursor: 'pointer', fontSize: '1.2em', marginLeft: 5 }}
      />
    </div>
    <ResponsiveContainer width={'100%'} height='80%'>
      <ComposedChart
        data={data}
        maxBarSize={50}
        margin={{
          top: 5,
          right: 30,
          left: 30,
          bottom: 5
        }}
      >
        <CartesianGrid strokeDasharray='1 1' />
        <XAxis
          dataKey='month'
        />
        <YAxis
          tickCount={6}
          domain={[Math.floor(min) - 5 < 0 ? 0 : Math.floor(min) - 5, Math.floor(max) + 5]}
          orientation='left'
          tickFormatter={(value) => formatNumber(value)}
          yAxisId='left'
          dataKey={'avgPrice'}
        >
          <Label
            value={t('Price (in {{priceLabel}})', { priceLabel })}
            angle={-90}
            position='insideLeft'
            offset={-5}
            style={{ textAnchor: 'middle' }} />
        </YAxis>
        <YAxis
          yAxisId='right'
          unit={'k'}
          tick={<CustomizedYAxisTick />}
          dataKey='maxAmount'
          orientation='right'
        >
          <Label
            value={t('Volume (in {{amountUnit}})', { amountUnit })}
            angle={-90}
            position='insideRight'
            offset={-15}
            style={{ textAnchor: 'middle' }} />
        </YAxis>
        <Tooltip content={<CustomTooltip />} animationEasing={'ease-out'} />
        <Bar
          unit={amountUnit}
          yAxisId={'right'}
          dataKey='confirmedAmount'
          name={prefixData.amount}
          stackId='a'
          fill={firstColor}
        />
        <Bar
          unit={amountUnit}
          yAxisId={'right'}
          dataKey='remainingAmount'
          stackId='a'
          name={t('Remaining amount')}
          fill='#82ca9d'
        />
        <Line
          yAxisId={'left'}
          type='monotone'
          name={prefixData.average}
          dataKey={'avgPrice'}
          unit={priceLabel}
          stroke={firstColor}
          dot={{ r: 5 }}
          activeDot={{ r: 8 }}
        />
        <Line
          yAxisId={'left'}
          type='monotone'
          unit={priceLabel}
          name={basePriceText?.translation}
          dataKey={'basePrice'}
          stroke={'black'}
          dot={{ r: 5 }}
          activeDot={{ r: 8 }}
        />
      </ComposedChart >
    </ResponsiveContainer>
    <Button as={Link} to={props.link} basic>{t('View complete report')}</Button>
  </ChartContainer >
}
export default DashboardChart